import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as enums from "../../enumerations";
import * as modelsGet from "../../models/get";
import * as modelsCalendar from "../../models/calendar";
import * as utils from "../../utils/utils";
import * as utilsCalendar from "../../utils/calendar";
import { CalendarMonthDay } from "./calendarMonthDay";
import { OptionalLink } from "../optionalLink";
import { Appointment } from "./appointment";
import { BlockedAppointment } from "./blockedAppointment";
import { AvailableAppointment } from "./availableAppointment";
import { OccupiedAppointment } from "./occupiedAppointment";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";

interface IProperties
{
    /**
     * Der angemeldete Benutzer.
     */
    currentUser: modelsGet.IUser,

    /**
     * Alle allgemeinen Einstellungen.
     */
    settings: {
        eventDayViewTemplate: string,
        showLabelsAsInitials: boolean
    },

    /**
     * Der selektierte Kalender oder die Kalendergruppe.
     */
    selectedObject: modelsCalendar.ISelectedObject,

    /**
     * Selektierte Datum.
     */
    selectedDate: moment.Moment,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar: modelsGet.ICalendar,

    /**
     * Die momentan selektierte Kalendaransicht.
     */
    selectedViewMode: enums.CalendarViewMode,

    /**
     * Die kalkulierte Zeitskala.
     */
    hours: modelsCalendar.IHour[],

    /**
     * Die Tage zum rendern der anderen Ansichten.
     */
    days: modelsCalendar.IDayOfWeek[],

    /**
     * Wird ausgeführt sobald sich das Popup für die Termindetails öffnet.
     * @param e Das auslösende Ereignis.
     * @param appointment Der gebundene Termin oder die Sperre.
     */
    onAppointmentPopupOpening?: (e: MouseEvent, appointment: modelsGet.IAppointment) => boolean
}

export class CalendarDay extends React.Component<IProperties>
{
    /**
     * Initialisiert die Komponente @see CalendarDay.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);
    }

    /**
     * Zu einem beliebigen Tag springen.
     */
    private onDayClick = (newDate: moment.Moment): void =>
    {
        //this.props.onDateChanged && this.props.onDateChanged(moment(newDate), "day")
        //console.log(newDate);
    }

    /**
     * Wird einmalig ausgeführt, wenn die Komponente im DOM gesetzt wird.
     */
    componentDidMount()
    {
        utilsCalendar.arrangeEvents();
        this.jumpToFirstAvailableTime();
    }

    /**
     * Wird immer ausgeführt wenn die Komponente aktualsiert wurde.
     */
    componentDidUpdate()
    {
        utilsCalendar.arrangeEvents();
    }

    /**
     * Springt zur ersten Verfügbaren Zeit in der Tages-, Wochen- oder Arbeitswochenansicht.
     */
    private jumpToFirstAvailableTime(): void
    {
        var { selectedViewMode, selectedCalendar } = this.props;

        if (selectedViewMode !== enums.CalendarViewMode.month && selectedCalendar != null)
        {
            var settings: modelsGet.ICalendarSettings = JSON.parse(selectedCalendar.settings);
            $(".calendar-dv-cols-container, [js-calendar-content]").scrollToTarget("#" + settings.time.range[0].removeAfter(":").concat("00").replace(":",""), false, false);
        }
    }

    /**
     * Bevor das Kontextmenü zum erstellen neuer Termine geöffnet wird, wird hier geprüft ob der
     * Benutzer das überhaupt darf.
     * @param e Das auslösende Ereignis.
     * @param dataSource Das Datum (samt Uhrzeit) an dem das Kontextmenü geöffnet werden soll.
     */
    private onContextMenuOpening = (e: MouseEvent, dataSource: { date: moment.Moment }): boolean =>
    {
        const { currentUser } = this.props;

        // Darf der Benutzer Termine in der Vergangenheit anlegen?.
        if (!currentUser.claims.can_create_event_in_the_past)
        {
            if (dataSource.date.isBefore(moment()))
                return false;
        }

        if (!currentUser.claims.can_create_event && !currentUser.claims.can_create_lock)
            return false;

        return true;
    }

    /**
     * Rendert den Header mit den Wochentagen.
     */
    private renderHeader(): JSX.Element[]
    {
        var { days } = this.props;
        var header: JSX.Element[] = []

        header.push(
            <table className="calendar-dv-header">
                <thead>
                    <th className="calendar-dv-header_col"></th>
                    {
                        days.map((day) =>
                        {
                            return (
                                <th className="calendar-dv-header_col">
                                    <div className="calendar-dv-header_col_text-container">
                                        <a className="calendar-dv-header_col_text">{day.header}</a>
                                    </div>
                                </th>
                            );
                        })
                    }
                </thead>
            </table>
        );

        return header;
    }

    /**
     * Rendert die anzuzeigenen Tage.
     * @param date Das Datum anhand dessen die Tage ermittelt werden sollen.
     * @returns Eine Komponente mit den Tagen die im Datepicker angzeigt werden sollen.
     */
    private renderDays(date: moment.Moment): JSX.Element
    {
        const {
            currentUser,
            settings,
            selectedObject,
            selectedCalendar,
            selectedViewMode,
            hours,
            days,
            onAppointmentPopupOpening
        } = this.props;

        var calendar: modelsGet.ICalendar = (selectedObject.type === enums.CalendarObjectType.calendar ? selectedObject.data as modelsGet.ICalendar : selectedCalendar);
        var showBlocks: boolean = utilsCalendar.showBlocks(selectedObject, selectedViewMode);

        return (
            <div className="calendar-dv-cols-container">
                <table className="calendar-dv-cols">
                    <tbody>
                        <tr className="calendar-dv-cols_row">
                            <td className="calendar-dv-cols_timeline">
                                {
                                    hours.map((hour) =>
                                    {
                                        let hourString: string = hour.hour.toShortTimeString();

                                        return (
                                            <div
                                                id={hourString.replace(":", "")}
                                                data-text={hourString}
                                                className="calendar-dv-cols_timeline_time">
                                                <div className="calendar-dv-cols_timeline_time_row"></div>
                                                {
                                                    hour.range.map((range) => <div className="calendar-dv-cols_timeline_time_row"></div>)
                                                }
                                            </div>
                                        );
                                    })
                                }
                            </td>
                            {
                                days.map((day) =>
                                {
                                    return (
                                        <td className="calendar-dv-cols_col">
                                            {
                                                hours.map((hour) =>
                                                {
                                                    let hourString: string = hour.hour.toShortTimeString();
                                                    let isTimeInRange: boolean = utilsCalendar.isTimeInRange(hourString, selectedViewMode, 0, selectedObject, selectedCalendar, moment(day.date));
                                                    let rowClassNames: string[] = ["calendar-dv-cols_col_time_row"];

                                                    if (!isTimeInRange)
                                                        rowClassNames.push("calendar-dv-cols_col_time_row--is-not-in-range");

                                                    return (
                                                        <div className="calendar-dv-cols_col_time">
                                                            <PopupProvider
                                                                id="day-contextmenu"
                                                                className={rowClassNames.join(" ")}
                                                                trigger={PopupTrigger.RightClick}
                                                                triggerStopPropagation={true}
                                                                isDisabled={currentUser.claims.can_create_event_when_locked ? false : !isTimeInRange}
                                                                dataSource={{
                                                                    date: moment(moment(day.date).startOf("day").format("L") + " " + hour.hour.toShortTimeString(), "DD.MM.YYYY HH:mm")
                                                                }}
                                                                onOpening={this.onContextMenuOpening}>
                                                                <div></div>
                                                            </PopupProvider>
                                                            {
                                                                hour.range.map((range) =>
                                                                {
                                                                    rowClassNames = ["calendar-dv-cols_col_time_row"];
                                                                    isTimeInRange = utilsCalendar.isTimeInRange(hourString, selectedViewMode, range, selectedObject, selectedCalendar, moment(day.date))
                                                                    if (!isTimeInRange)
                                                                        rowClassNames.push("calendar-dv-cols_col_time_row--is-not-in-range");

                                                                    return (
                                                                        <PopupProvider
                                                                            id="day-contextmenu"
                                                                            className={rowClassNames.join(" ")}
                                                                            trigger={PopupTrigger.RightClick}
                                                                            triggerStopPropagation={true}
                                                                            isDisabled={currentUser.claims.can_create_event_when_locked ? false : !isTimeInRange}
                                                                            dataSource={{
                                                                                date: moment(moment(day.date).startOf("day").format("L") + " " + hour.hour.Hours + ":" + range, "DD.MM.YYYY HH:mm")
                                                                            }}
                                                                            onOpening={this.onContextMenuOpening}>
                                                                            <div></div>
                                                                        </PopupProvider>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    );
                                                })
                                            }
                                            {
                                                day.appointments && day.appointments.length > 0 && day.appointments.where(a => a.appointment_type !== "block").map((appointment) =>
                                                {
                                                    let showEvent: number = utilsCalendar.showEvent(appointment, currentUser, selectedViewMode, selectedObject, selectedCalendar);
                                                    if (showEvent === 3)
                                                        return null;

                                                    let eventPosition = utilsCalendar.getEventPosition(appointment);

                                                    return (
                                                        <PopupProvider
                                                            style={{
                                                                position: "absolute",
                                                                top: eventPosition.base.top
                                                            }}
                                                            id="event-contextmenu"
                                                            trigger={PopupTrigger.RightClick}
                                                            triggerStopPropagation={true}
                                                            dataSource={{
                                                                calendar: 0,
                                                                appointment: appointment
                                                            }}
                                                            attributeName="event"
                                                            isDisabled={showEvent === 2}>
                                                            <PopupProvider
                                                                id="event-info"
                                                                trigger={PopupTrigger.Hover}
                                                                triggerDelay={1000}
                                                                triggerStopPropagation={true}
                                                                dataSource={appointment}
                                                                onOpening={onAppointmentPopupOpening}>
                                                                <Appointment
                                                                    currentSelectedCalendar={calendar}
                                                                    eventDayViewTemplate={settings.eventDayViewTemplate}
                                                                    showLabelsAsInitials={settings.showLabelsAsInitials}
                                                                    appointment={appointment}
                                                                    isForMonthView={false}
                                                                    isSubjectBold={true} />
                                                            </PopupProvider>
                                                        </PopupProvider>
                                                    );
                                                })
                                            }
                                            {
                                                day.appointments && day.appointments.length > 0 && day.appointments.where(a => a.appointment_type === "block").map((appointment) =>
                                                {
                                                    let lockPosition = utilsCalendar.getLockPosition(appointment);

                                                    return (
                                                        <PopupProvider
                                                            style={{
                                                                position: "absolute",
                                                                top: lockPosition.top
                                                            }}
                                                            id="event-contextmenu"
                                                            trigger={PopupTrigger.RightClick}
                                                            triggerStopPropagation={true}
                                                            dataSource={{
                                                                calendar: 0,
                                                                appointment: appointment
                                                            }}
                                                            attributeName="event">
                                                            <PopupProvider
                                                                id="event-info"
                                                                trigger={PopupTrigger.Hover}
                                                                triggerDelay={1000}
                                                                triggerStopPropagation={true}
                                                                dataSource={appointment}
                                                                onOpening={onAppointmentPopupOpening}>
                                                                <BlockedAppointment
                                                                    currentSelectedCalendar={calendar}
                                                                    appointment={appointment}
                                                                    isForMonthView={false}
                                                                    isSubjectBold={true}
                                                                    isDisabled={!currentUser.claims.can_create_lock} />
                                                            </PopupProvider>
                                                        </PopupProvider>
                                                    );
                                                })
                                            }
                                            {
                                                day.availableAppointments && day.availableAppointments.length > 0 && day.availableAppointments.map((appointment) =>
                                                {
                                                    if (!showBlocks)
                                                        return null;

                                                    let blockPosition = utilsCalendar.getLockPosition(appointment);

                                                    return (
                                                        <PopupProvider
                                                            style={{
                                                                position: "absolute",
                                                                top: blockPosition.top,
                                                                height: blockPosition.height,
                                                                padding: blockPosition.padding
                                                            }}
                                                            id="day-contextmenu"
                                                            trigger={PopupTrigger.RightClick}
                                                            triggerStopPropagation={true}
                                                            dataSource={{
                                                                date: appointment.starttime,
                                                                enddate: appointment.endtime,
                                                                calendar: null
                                                            }}
                                                            attributeName="event">
                                                            <AvailableAppointment
                                                                currentSelectedCalendar={calendar}
                                                                appointment={appointment}
                                                                isPositionAbsolute={false}
                                                                userCanCreateAppointmentInThePast={currentUser.claims.can_create_event_in_the_past} />
                                                        </PopupProvider>
                                                    );
                                                })
                                            }
                                            {
                                                day.occupiedAppointments && day.occupiedAppointments.length > 0 && day.occupiedAppointments.map((appointment) =>
                                                {
                                                    if (!showBlocks)
                                                        return null;

                                                    if (!appointment.isForCompleteAppointment)
                                                    {
                                                        if (!utilsCalendar.showLockedBlock(appointment, currentUser))
                                                            return null;
                                                    }

                                                    return (
                                                        <OccupiedAppointment appointment={appointment} />
                                                    );
                                                })
                                            }
                                        </td>
                                    );
                                })
                            }
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    /**
     * Rendert die Komponente.
     */
    public render(): JSX.Element
    {
        const { selectedDate, days } = this.props;

        if (days != null)
        {
            return (
                <div
                    className="calendar-day">
                    {this.renderHeader()}
                    {this.renderDays(selectedDate)}
                </div>
            );
        }
        else
        {
            return null;
        }
    }
}